import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, query, where, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';

const MAPS_COLLECTION = 'maps';
const PINS_COLLECTION = 'pins';

/**
 * Uploads a map image to Firebase Storage
 */
export const uploadMapImage = async (file, mapId) => {
  try {
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const allowedFormats = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    
    if (!allowedFormats.includes(fileExtension)) {
      throw new Error('Invalid file format. Please use JPG, PNG, GIF, WebP, or BMP.');
    }
    
    const fileName = `maps/${mapId}/map.${fileExtension}`;
    const storageRef = ref(storage, fileName);
    
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading map image:', error);
    throw error;
  }
};

/**
 * Creates a new map
 */
export const createMap = async (mapData) => {
  try {
    const docRef = await addDoc(collection(db, MAPS_COLLECTION), {
      ...mapData,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating map:', error);
    throw error;
  }
};

/**
 * Gets all maps
 */
export const getMaps = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, MAPS_COLLECTION));
    
    const maps = [];
    querySnapshot.forEach((doc) => {
      maps.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt on the client side to avoid index requirements
    maps.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return bTime - aTime; // Newest first
    });
    
    return maps;
  } catch (error) {
    console.error('Error fetching maps:', error);
    throw error;
  }
};

/**
 * Gets a specific map by ID
 */
export const getMapById = async (mapId) => {
  try {
    const docRef = doc(db, MAPS_COLLECTION, mapId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error('Map not found');
    }
  } catch (error) {
    console.error('Error fetching map:', error);
    throw error;
  }
};

/**
 * Updates a map
 */
export const updateMap = async (mapId, updates) => {
  try {
    const mapRef = doc(db, MAPS_COLLECTION, mapId);
    await updateDoc(mapRef, {
      ...updates,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating map:', error);
    throw error;
  }
};

/**
 * Adds a pin to a map
 */
export const addPin = async (mapId, pinData) => {
  try {
    const docRef = await addDoc(collection(db, PINS_COLLECTION), {
      ...pinData,
      mapId,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding pin:', error);
    throw error;
  }
};

/**
 * Gets all pins for a specific map
 */
export const getPinsByMapId = async (mapId) => {
  try {
    const querySnapshot = await getDocs(
      query(
        collection(db, PINS_COLLECTION),
        where('mapId', '==', mapId)
      )
    );
    
    const pins = [];
    querySnapshot.forEach((doc) => {
      pins.push({ id: doc.id, ...doc.data() });
    });
    
    // Sort by createdAt on the client side to avoid composite index requirement
    pins.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return aTime - bTime;
    });
    
    return pins;
  } catch (error) {
    console.error('Error fetching pins:', error);
    throw error;
  }
};

/**
 * Updates a pin
 */
export const updatePin = async (pinId, updates) => {
  try {
    const pinRef = doc(db, PINS_COLLECTION, pinId);
    await updateDoc(pinRef, {
      ...updates,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating pin:', error);
    throw error;
  }
};

/**
 * Deletes a pin
 */
export const deletePin = async (pinId) => {
  try {
    await deleteDoc(doc(db, PINS_COLLECTION, pinId));
  } catch (error) {
    console.error('Error deleting pin:', error);
    throw error;
  }
};

/**
 * Deletes a map and all its pins
 */
export const deleteMap = async (mapId) => {
  try {
    // Delete all pins for this map
    const pins = await getPinsByMapId(mapId);
    const deletePromises = pins.map(pin => deletePin(pin.id));
    await Promise.all(deletePromises);
    
    // Delete the map document
    await deleteDoc(doc(db, MAPS_COLLECTION, mapId));
    
    // TODO: Delete the map image from storage if needed
  } catch (error) {
    console.error('Error deleting map:', error);
    throw error;
  }
};

/**
 * Pin categories with their display information
 */
export const PIN_CATEGORIES = [
  { value: 'location', label: '🏰 Location', color: '#d98d2a', description: 'Cities, towns, landmarks' },
  { value: 'danger', label: '⚠️ Danger', color: '#ff6b6b', description: 'Dangerous areas, monsters' },
  { value: 'treasure', label: '💰 Treasure', color: '#ffd700', description: 'Hidden treasures, loot' },
  { value: 'npc', label: '👤 NPC', color: '#4ecdc4', description: 'Important characters' },
  { value: 'quest', label: '📜 Quest', color: '#a78bfa', description: 'Quest locations, objectives' },
  { value: 'shop', label: '🏪 Shop', color: '#10b981', description: 'Merchants, services' },
  { value: 'inn', label: '🏨 Inn/Tavern', color: '#f59e0b', description: 'Rest areas, inns' },
  { value: 'dungeon', label: '🕳️ Dungeon', color: '#6b7280', description: 'Dungeons, caves, ruins' },
  { value: 'portal', label: '🌀 Portal', color: '#9c27b0', description: 'Magical portals, teleports' },
  { value: 'shrine', label: '⛪ Shrine', color: '#fff176', description: 'Religious sites, temples' },
  { value: 'camp', label: '⛺ Camp', color: '#8d6e63', description: 'Campsites, rest spots' },
  { value: 'other', label: '📍 Other', color: '#8b5cf6', description: 'Miscellaneous points' }
];